import { authApiV2 } from '../axiosWrapper';
import { 
    trackEvent,
    trackExceptionWithContext 
} from './application-insights/appInsights';
import { 
    AppInsightsActions, 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from './application-insights/appInsightsConstants';

// Email request types matching your API
export interface IAttachmentRequest {
    filename: string;
    content: string; // base64 encoded content
    contentType: string;
    contentId?: string;
}

export interface ISendSingleEmailRequest {
    recipient: string;
    subject: string;
    body: string;
    isHtml?: boolean;
    ccRecipient?: string;
    bccRecipient?: string;
    attachments?: IAttachmentRequest[];
}

export interface ISendEmailResponse {
    isSuccess: boolean;
    emailId?: string;
    errorMessage?: string;
}


export class EmailService {
    private static readonly BASE_PATH = '/notifications/emails';

    /**
     * Send a single email
     */
    static async sendSingleEmail(request: ISendSingleEmailRequest): Promise<ISendEmailResponse> {
        const requestId = `email-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        try {
            trackEvent(AppInsightsActions.EMAIL_SEND_ATTEMPT, {
                requestId,
                component: AppInsightsComponents.EMAIL_SERVICE,
                recipient: request.recipient,
                hasAttachments: !!(request.attachments && request.attachments.length > 0),
                attachmentCount: request.attachments?.length || 0,
                isHtml: request.isHtml ?? false
            });

            const response = await authApiV2.post<ISendEmailResponse>(
                `${this.BASE_PATH}/send`,
                {
                    recipient: request.recipient,
                    subject: request.subject,
                    body: request.body,
                    isHtml: request.isHtml ?? true,
                    ccRecipient: request.ccRecipient,
                    bccRecipient: request.bccRecipient,
                    attachments: request.attachments
                }
            );

            trackEvent(AppInsightsActions.EMAIL_SEND_SUCCESS, {
                requestId,
                component: AppInsightsComponents.EMAIL_SERVICE,
                recipient: request.recipient,
                emailId: response.data.emailId ?? ''
            });

            return response.data;
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to send email';
            
            trackExceptionWithContext(
                error instanceof Error ? error : new Error(errorMessage),
                {
                    component: AppInsightsComponents.EMAIL_SERVICE,
                    action: AppInsightsActions.EMAIL_SEND_FAILURE,
                    additionalData: {
                        requestId,
                        errorType: AppInsightsErrorTypes.EMAIL_SERVICE_ERROR,
                        recipient: request.recipient,
                        statusCode: error.response?.status,
                        errorMessage: errorMessage
                    }
                }
            );
            
            throw new Error(errorMessage);
        }
    }

    /**
     * Send notification email (convenience method for common notifications)
     */
    static async sendNotificationEmail(
        recipient: string,
        subject: string,
        message: string,
        options?: {
            isHtml?: boolean;
            ccRecipient?: string;
            bccRecipient?: string;
        }
    ): Promise<ISendEmailResponse> {
        return this.sendSingleEmail({
            recipient,
            subject,
            body: message,
            isHtml: options?.isHtml ?? true,
            ccRecipient: options?.ccRecipient,
            bccRecipient: options?.bccRecipient
        });
    }

    /**
     * Send email with file attachments
     */
    static async sendEmailWithAttachments(
        recipient: string,
        subject: string,
        body: string,
        attachments: File[],
        options?: {
            isHtml?: boolean;
            ccRecipient?: string;
            bccRecipient?: string;
        }
    ): Promise<ISendEmailResponse> {
        const attachmentRequests: IAttachmentRequest[] = await Promise.all(
            attachments.map(async (file) => {
                const arrayBuffer = await file.arrayBuffer();
                const base64Content = btoa(
                    new Uint8Array(arrayBuffer).reduce(
                        (data, byte) => data + String.fromCharCode(byte),
                        ''
                    )
                );

                return {
                    filename: file.name,
                    content: base64Content,
                    contentType: file.type || 'application/octet-stream'
                };
            })
        );

        return this.sendSingleEmail({
            recipient,
            subject,
            body,
            isHtml: options?.isHtml ?? true,
            ccRecipient: options?.ccRecipient,
            bccRecipient: options?.bccRecipient,
            attachments: attachmentRequests
        });
    }
}